<template>
  <v-dialog v-model="show" persistent width="45%">
    <v-card class="rounded-lg" style="border: 3px solid #3f51b5;">
      <v-card-title color="white" class="ma-0 pa-2" primary-title>
        <span
          v-if="colorPickerApplyToSelected"
          class="primary--text text--darken-3 title"
          >Choose colors/fill patterns for {{ selectedCohorts.length }} selected
          study groups</span
        >
        <span v-else class="primary--text text--darken-3 title"
          >Choose color/fill pattern for study group "{{
            cohort ? cohort.label : ''
          }}"</span
        >
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="py-4">
        <!-- color(s) -->
        <v-tabs v-model="colorTab">
          <v-tab key="single" :disabled="colorPickerApplyToSelected"
            >Single Color</v-tab
          >
          <v-tab key="brewer" :disabled="!colorPickerApplyToSelected"
            >Predefined Palettes</v-tab
          >
          <v-tab key="gradient" :disabled="!colorPickerApplyToSelected"
            >Custom Gradient</v-tab
          >
        </v-tabs>

        <v-tabs-items v-model="colorTab" class="pt-3">
          <v-tab-item key="single" :eager="true">
            <v-container class="ma-0 pa-0">
              <v-row class="ma-0 pa-0">
                <v-col cols="12" class="ma-0 pa-0">
                  Select color:
                  <v-color-picker
                    v-model="colorPickerColor"
                    class="pt-2 pb-4 mb-2"
                    mode="rgba"
                    show-swatches
                    hide-canvas
                    hide-inputs
                    hide-sliders
                    swatches-max-height="400"
                    width="100%"
                    @update:color="singleColorChanged"
                  >
                  </v-color-picker>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>

          <v-tab-item key="brewer" :eager="true">
            <v-container class="pa-0 ma-0">
              <v-row class="pa-0 ma-0">
                <v-col cols="12" class="pa-0 ma-0">
                  <v-radio-group v-model="paletteGroup" row>
                    <v-radio
                      key="pg-ssc"
                      label="sequential"
                      value="ssc"
                    ></v-radio>
                    <v-radio key="pg-dc" label="diverging" value="dc"></v-radio>
                    <v-radio
                      key="pg-cat"
                      :disabled="selectedCohorts.length > 12"
                      label="categorical"
                      value="cat"
                    ></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-radio-group v-model="palette">
                <v-row v-if="paletteGroup == 'ssc'" class="pb-2">
                  <!-- single color, 1st column -->
                  <v-col cols="6">
                    <v-container class="pa-0 ma-0">
                      <v-row v-if="paletteGroup == 'ssc'" class="pb-2">
                        <v-col cols="12">
                          single color ColorBrewer:<br clear="both" />
                        </v-col>
                      </v-row>

                      <v-row
                        v-for="(cgroup, index) in [
                          'Blues',
                          'Greens',
                          'Greys',
                          'Oranges',
                          'Purples',
                          'Reds',
                        ]"
                        :key="'bssp-' + index"
                        class="pa-0 ma-0 py-1"
                      >
                        <v-col cols="4" class="pa-0 ma-0">
                          <v-radio
                            :key="'ss-vrg-' + index"
                            :label="cgroup"
                            :value="cgroup"
                          ></v-radio>
                        </v-col>
                        <v-col cols="8" class="pa-0 ma-0">
                          <v-chip
                            v-for="(c, i) in selectedCohorts"
                            :key="'sscc-' + index + '-' + i"
                            small
                            label
                            class="ml-1"
                            :color="
                              getBrewerColor(cgroup, selectedCohorts.length, i)
                            "
                          ></v-chip>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>

                  <!-- single color, 2nd column -->
                  <v-col cols="6">
                    <v-container class="pa-0 ma-0">
                      <v-row class="pb-2">
                        <v-col cols="12">
                          multi color ColorBrewer:<br clear="both" />
                        </v-col>
                      </v-row>

                      <v-row
                        v-for="(cgroup, index) in [
                          'BuGn',
                          'BuPu',
                          'GnBu',
                          'OrRd',
                          'PuBu',
                          'PuBuGn',
                          'PuRd',
                          'RdPu',
                          'YlGn',
                          'YlGnBu',
                          'YlOrBr',
                          'YlOrRd',
                        ]"
                        :key="'bsmp-' + index"
                        class="pa-0 ma-0 py-1"
                      >
                        <v-col cols="4" class="pa-0 ma-0">
                          <v-radio
                            :key="'ms-vrg-' + index"
                            :label="cgroup"
                            :value="cgroup"
                          ></v-radio>
                        </v-col>
                        <v-col cols="8" class="pa-0 ma-0">
                          <v-chip
                            v-for="(c, i) in selectedCohorts"
                            :key="'smcc-' + index + '-' + i"
                            small
                            label
                            class="ml-1"
                            :color="
                              getBrewerColor(cgroup, selectedCohorts.length, i)
                            "
                          ></v-chip>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>
                </v-row>

                <div v-if="paletteGroup == 'dc'">
                  <v-row class="pb-2">
                    <v-col cols="12">
                      diverging ColorBrewer:<br clear="both" />
                    </v-col>
                  </v-row>

                  <v-row
                    v-for="(cgroup, index) in [
                      'BrBG',
                      'PRGn',
                      'PiYG',
                      'PuOr',
                      'RdBu',
                      'RdGy',
                      'RdYlBu',
                      'RdYlGn',
                      'Spectral',
                    ]"
                    :key="'bdp-' + index"
                    class="pa-0 ma-0 py-1"
                  >
                    <v-col cols="2" class="pa-0 ma-0">
                      <v-radio
                        :key="'d-vrg-' + index"
                        :label="cgroup"
                        :value="cgroup"
                      ></v-radio>
                    </v-col>
                    <v-col cols="10" class="pa-0 ma-0">
                      <v-chip
                        v-for="(c, i) in selectedCohorts"
                        :key="'dcc-' + index + '-' + i"
                        small
                        label
                        class="ml-1"
                        :color="
                          getBrewerColor(cgroup, selectedCohorts.length, i)
                        "
                      ></v-chip>
                    </v-col>
                  </v-row>
                </div>

                <!-- qualitative palettes only support up to 12 classes -->
                <div v-if="paletteGroup == 'cat'">
                  <v-row class="pb-2">
                    <v-col cols="12">
                      qualitative/categorical ColorBrewer:<br clear="both" />
                    </v-col>
                  </v-row>

                  <v-row
                    v-for="(cgroup, index) in getBrewerQualitativePalettes(
                      selectedCohorts.length
                    )"
                    :key="'bqp-' + index"
                    class="pa-0 ma-0 py-1"
                  >
                    <v-col cols="2" class="pa-0 ma-0">
                      <v-radio
                        :key="'q-vrg-' + index"
                        :label="cgroup"
                        :value="cgroup"
                      ></v-radio>
                    </v-col>
                    <v-col cols="10" class="pa-0 ma-0">
                      <v-chip
                        v-for="(c, i) in selectedCohorts"
                        :key="'qcc-' + index + '-' + i"
                        small
                        label
                        class="ml-1"
                        :color="
                          getBrewerColor(cgroup, selectedCohorts.length, i)
                        "
                      ></v-chip>
                    </v-col>
                  </v-row>
                </div>
              </v-radio-group>
            </v-container>
          </v-tab-item>

          <v-tab-item key="gradient" :eager="true">
            <v-container>
              <v-row>
                <v-col cols="6">
                  <span>start color:</span>
                  <v-color-picker
                    v-model="colorPickerColor"
                    class="pt-2"
                    mode="rgba"
                    @update:color="gradientChanged"
                  >
                  </v-color-picker>
                </v-col>

                <v-col cols="6">
                  <span>end color:</span>
                  <v-color-picker
                    v-model="colorPickerColor2"
                    class="pt-2"
                    mode="rgba"
                    @update:color="gradientChanged"
                  >
                  </v-color-picker>
                </v-col>
              </v-row>
            </v-container>
          </v-tab-item>
        </v-tabs-items>

        <!-- fill pattern(s) -->
        <v-container v-if="showFillPatterns" class="ma-0 pa-0">
          <v-row class="ma-0 pa-0">
            <v-col cols="6" class="ma-0 pa-0">
              Select (optional) fill pattern:
            </v-col>
            <v-col cols="6" class="ma-0 pa-0">
              <v-select
                v-model="patternAssignmentMode"
                :items="patternAssignmentOptions"
                item-value="value"
                item-text="label"
                :disabled="colorTab === 0"
                label="Assign patterns to study groups"
                class="pa-0 ma-0 pr-3"
                hide-details
                row
              >
              </v-select>
            </v-col>
          </v-row>

          <v-row class="ma-0 pa-0 pt-2">
            <v-col cols="12" class="ma-0 pa-0">
              <div
                v-for="(p, index) in patterns"
                :key="'sfp-' + index"
                class="px-1 ma-0 pt-1 float-left"
                :style="getPatternBlockStyle(index)"
                @click="patternClicked(index)"
              >
                <svg width="40" height="25">
                  <g>
                    <rect
                      x="0"
                      y="0"
                      rx="0"
                      ry="0"
                      border-radius="0%"
                      width="40"
                      height="25"
                      fill="#d0d0d0"
                      stroke="black"
                    />
                    <rect
                      x="0"
                      y="0"
                      rx="0"
                      ry="0"
                      border-radius="0%"
                      width="40"
                      height="25"
                      :fill="'url(#' + p['id'] + ')'"
                    />
                  </g>
                </svg>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn text color="red lighten-2" @click="closeColorPickerDialog(true)">
          <v-icon left>close</v-icon> Cancel
        </v-btn>

        <v-btn
          class="primary white--text ma-0 px-2 mx-2"
          @click="closeColorPickerDialog(false)"
        >
          Ok
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import { actions } from '@/store/modules/dataExplorer/types';
import { scaleLinear } from 'd3-scale';
import { color } from 'd3-color';
import { patterns } from '@/utils/patterns';
import { getBrewerColor, getBrewerQualitativePalettes } from '@/utils/colors';

let PATTERN_OPTIONS = [
  { value: 'single', label: 'Same pattern for all groups' },
  { value: 'consecutive-1', label: 'Consecutive patterns' },
  { value: 'consecutive-4', label: 'Every 4th pattern' },
];

export default {
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    showFillPatterns: {
      type: Boolean,
      required: false,
    },
    cohort: {
      type: Object,
      required: false,
      default: null,
    },
    cohorts: {
      type: Array,
      required: true,
    },
    selectedCohorts: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      colorTab: 'brewer',
      palette: null,
      paletteGroup: 'ssc',
      colorPickerOriginalColorsAndPatterns: null,
      colorPickerColor: null,
      colorPickerColor2: null,
      colorPickerApplyToSelected: false,
      patterns: patterns,
      colorPickerSelectedPatternInd: null,
      patternAssignmentMode: PATTERN_OPTIONS[0],
      patternAssignmentOptions: PATTERN_OPTIONS,
      getBrewerColor: getBrewerColor,
      getBrewerQualitativePalettes: getBrewerQualitativePalettes,
    };
  },
  computed: {
    sortedSelectedCohorts() {
      let sel = this.selectedCohorts;
      let sel_ids = {};
      sel.forEach(c => {
        sel_ids[c['id']] = 1;
      });
      let sel_sorted = [];
      this.cohorts.forEach(c => {
        if (c.id in sel_ids) {
          sel_sorted.push(c);
        }
      });
      return sel_sorted;
    },
  },
  watch: {
    show(nshow) {
      // open dialog
      if (nshow) {
        // multiple cohorts
        if (this.cohort == null) {
          this.colorPickerApplyToSelected = true;
          this.colorTab = 1;
          // set gradient based on first and last cohort
          let ssel = this.sortedSelectedCohorts;
          this.colorPickerColor = this.colorStringToRgbObj(ssel[0]['color']);
          this.colorPickerColor2 = this.colorStringToRgbObj(
            ssel[ssel.length - 1]['color']
          );
          this.colorPickerSelectedPatternInd = null;
          this.palette = null;
        }
        // single cohort
        else {
          this.colorPickerApplyToSelected = false;
          this.colorTab = 0;
          this.colorPickerCohort = this.cohort;
          this.colorPickerColor = this.colorStringToRgbObj(this.cohort.color);
          this.colorPickerColor2 = this.colorStringToRgbObj(this.cohort.color);
          this.colorPickerSelectedPatternInd = null;
        }

        // save color assignments for all study groups
        this.colorPickerOriginalColorsAndPatterns = {};
        this.cohorts.forEach(sg => {
          this.colorPickerOriginalColorsAndPatterns[sg.id] = {
            color: sg.color,
            pattern: sg.pattern,
          };
        });
      }
    },
    palette(np) {
      if (np != null) {
        this.updateColors(np);
      }
    },
    colorPickerSelectedPatternInd(npi) {
      if (npi != null) {
        this.updateColors(null);
      }
    },
    patternAssignmentMode() {
      this.updateColors(this.palette);
    },
  },
  methods: {
    ...mapActions('dataExplorer', {
      saveCohortDisplayPrefs: actions.SAVE_COHORT_DISPLAY_PREFS,
    }),
    updateColors(palette) {
      // convert from v-color-picker rgb object to rgb string
      let c1 = this.colorPickerColor;
      let c1str = `rgb(${c1.r}, ${c1.g}, ${c1.b})`;

      // multiple study group change
      if (this.colorPickerApplyToSelected) {
        // apply consecutive patterns in the order the cohorts appear in the original list
        let ssel = this.sortedSelectedCohorts;
        let n_cohorts = ssel.length;
        let n_patterns = this.patterns.length;
        let c2 = this.colorPickerColor2;
        let c2str = `rgb(${c2.r}, ${c2.g}, ${c2.b})`;

        // custom color gradient
        let colorGrad = scaleLinear()
          .domain([1, n_cohorts])
          .range([color(c1str).formatHex(), color(c2str).formatHex()]);

        for (let g = 0; g < n_cohorts; ++g) {
          // on palette tab
          let col = null;
          if (this.colorTab == 1) {
            col =
              palette == null
                ? ssel[g].color
                : this.getBrewerColor(palette, this.selectedCohorts.length, g);
          } else {
            col = color(colorGrad(g + 1)).formatRgb();
          }
          let cevt = {
            cohort: ssel[g],
            color: col,
          };

          if (this.colorPickerSelectedPatternInd != null) {
            // default - same pattern for all groups
            let offset = 0;
            if (this.patternAssignmentMode === 'consecutive-1') {
              offset = g;
            } else if (this.patternAssignmentMode === 'consecutive-4') {
              offset = g * 4;
            }
            cevt['pattern'] = this.patterns[
              (this.colorPickerSelectedPatternInd + offset) % n_patterns
            ];
          }
          this.$emit('cohortColorChange', cevt);
        }
      }
      // single study group change
      else if (this.colorPickerCohort != null) {
        let evt = {
          cohort: this.colorPickerCohort,
          color: c1str,
        };
        if (this.colorPickerSelectedPatternInd != null) {
          evt.pattern = this.patterns[this.colorPickerSelectedPatternInd];
        }
        this.$emit('cohortColorChange', evt);
      }
    },
    gradientChanged() {
      this.palette = null;
      // do not update unless on the gradient tab
      if (this.colorTab == 2) {
        this.updateColors(null);
      }
    },
    singleColorChanged() {
      // do not update unless on the single color tab
      if (this.colorTab == 0) {
        this.updateColors(null);
      }
    },
    closeColorPickerDialog(cancel) {
      let cohortUpdates = [];
      this.cohorts
        .filter(sg => sg.id != 'patient')
        .forEach(sg => {
          // color or pattern has been changed
          if (
            sg.color !=
              this.colorPickerOriginalColorsAndPatterns[sg.id]['color'] ||
            (sg.pattern != null &&
              sg.pattern.ind !=
                this.colorPickerOriginalColorsAndPatterns[sg.id]['pattern'][
                  'ind'
                ])
          ) {
            // reset cohort to original color and pattern
            if (cancel) {
              this.$emit('cohortColorChange', {
                cohort: sg,
                color: this.colorPickerOriginalColorsAndPatterns[sg.id][
                  'color'
                ],
                pattern: this.colorPickerOriginalColorsAndPatterns[sg.id][
                  'pattern'
                ],
              });
            }
            // save new cohort color and pattern
            else {
              if (sg.pattern == null || sg.pattern == '') {
                cohortUpdates.push({
                  cohort_id: sg.id,
                  color: sg.color,
                  pattern: 'solid',
                });
              } else {
                cohortUpdates.push({
                  cohort_id: sg.id,
                  color: sg.color,
                  pattern: sg.pattern.id,
                });
              }
            }
          }
        });
      if (cohortUpdates.length > 0) this.saveCohortDisplayPrefs(cohortUpdates);
      this.$emit('dialogClosed', true);
    },
    colorStringToRgbObj(cstr) {
      let c = color(cstr);
      return c.rgb();
    },
    patternClicked(i) {
      this.colorPickerSelectedPatternInd = i;
    },
    getPatternBlockStyle(i) {
      if (this.colorPickerSelectedPatternInd === i) {
        return 'background-color: #ffd0d0;';
      }
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
.v-list {
  height: 200px;
  overflow-y: auto;
}
</style>
